import React, { useMemo } from 'react';
import './Pagination.css';

/** Máximo de registros por página (búsqueda / listado de servicios). */
export const MAX_PAGE_SIZE = 200;

export const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 200] as const;

type PaginationProps = {
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Si se define, muestra selector de registros por página (p. ej. servicios 5–100). */
  onPageSizeChange?: (pageSize: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages: _totalPagesFromApi,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const safePageSize = Math.min(Math.max(pageSize, 1), MAX_PAGE_SIZE);
  const totalPagesSafe =
    totalCount > 0 ? Math.ceil(totalCount / Math.max(safePageSize, 1)) : 1;

  const startItem = totalCount === 0 ? 0 : (page - 1) * safePageSize + 1;
  const endItem = Math.min(page * safePageSize, totalCount);

  const handlePrevious = () => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPagesSafe) {
      onPageChange(page + 1);
    }
  };

  const sizeOptions = useMemo((): number[] => {
    const o: number[] = [...PAGE_SIZE_OPTIONS];
    const clamped = Math.min(pageSize, MAX_PAGE_SIZE);
    if (clamped >= 1 && !o.includes(clamped)) {
      o.push(clamped);
      o.sort((a, b) => a - b);
    }
    return o.filter((n) => n <= MAX_PAGE_SIZE);
  }, [pageSize]);

  if (totalCount === 0) {
    return null;
  }

  const showPageNav = totalPagesSafe > 1;
  const showPageSize = Boolean(onPageSizeChange);

  /* Sin selector de tamaño: mismo criterio que antes (solo barra si hay más de una página). */
  if (!onPageSizeChange && !showPageNav) {
    return null;
  }

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        {startItem}–{endItem} de {totalCount.toLocaleString("es-CL")}
      </div>
      <div className="pagination-toolbar">
        {showPageSize && (
          <label className="pagination-page-size-label">
            Por pág.
            <select
              className="pagination-page-size-select"
              value={safePageSize}
              onChange={(e) =>
                onPageSizeChange?.(Math.min(Number(e.target.value), MAX_PAGE_SIZE))
              }
              aria-label="Registros por página"
            >
              {sizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {showPageNav && (
          <div className="pagination-controls">
            <button
              type="button"
              className="pagination-btn"
              onClick={handlePrevious}
              disabled={page === 1}
              aria-label="Página anterior"
            >
              Anterior
            </button>
            <span className="pagination-page-info" aria-live="polite">
              {page} / {totalPagesSafe}
            </span>
            <button
              type="button"
              className="pagination-btn"
              onClick={handleNext}
              disabled={page >= totalPagesSafe}
              aria-label="Página siguiente"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
